import { useState, useCallback, useRef } from 'react';
import { aiSearchFaqs, FAQ } from '../../../../../service/faqService';
import { getConciergeSearch } from '../../../../../service/serviceService';

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  kind: 'text' | 'card';
  text: string;
  displayText?: string;
  streaming?: boolean;
  done?: boolean;
  feedback?: 'yes' | 'no' | null;
  // Rich card fields
  providerName?: string;
  providerService?: string;
  rating?: number;
  reviewCount?: number;
  price?: number;
  serviceId?: string;
  displayId?: string;
  slug?: string;
}

// FAQ topic keys that trigger a conciergeSearch provider card.
// Mirrors the design's `card: true` FAQ entries (booking-related).
const BOOKING_TOPIC_KEYS = [
  'book a service',
  'book a provider',
  'how do i book',
  'booking',
  'find a provider',
];

const DEFAULT_SUGGESTIONS = [
  'What is HirePanther?',
  'How do I book a service?',
];

// Derive suggestion chips from the FAQ search results. Uses other matched FAQ
// questions when available, otherwise falls back to static defaults. Trivial
// to swap for server-provided suggestions when the backend adds them.
const getSuggestions = (faqs: FAQ[], bestFaq: FAQ): string[] => {
  const others = faqs
    .filter((f) => f.faqId !== bestFaq.faqId)
    .map((f) => f.question)
    .slice(0, 3);
  if (others.length > 0) return others;
  return DEFAULT_SUGGESTIONS;
};

const isBookingRelated = (faq: FAQ): boolean => {
  const lower = faq.question.toLowerCase();
  return BOOKING_TOPIC_KEYS.some((key) => lower.includes(key));
};

let nextId = 1;

const GREETING: ChatMessage = {
  id: 0,
  role: 'assistant',
  kind: 'text',
  text: "Hi! I'm your AI assistant — ask me anything about HirePanther in your own words, no need for exact phrasing.",
  displayText:
    "Hi! I'm your AI assistant — ask me anything about HirePanther in your own words, no need for exact phrasing.",
  streaming: false,
  done: true,
  feedback: null,
};

const FALLBACK_TEXT =
  "I'm still learning that one — but I can help with bookings, verified providers, service coverage, and payments. Try asking how bookings work.";

export function useChatSession() {
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING]);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>(DEFAULT_SUGGESTIONS);
  const streamRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopStreaming = useCallback(() => {
    if (streamRef.current) {
      clearInterval(streamRef.current);
      streamRef.current = null;
    }
  }, []);

  // Cosmetic streaming reveal: character-by-character after the REST response
  const startStreaming = useCallback(
    (msgId: number, fullText: string) => {
      stopStreaming();
      let i = 0;
      streamRef.current = setInterval(() => {
        i += Math.max(1, Math.round(fullText.length / 60));
        const slice = fullText.slice(0, i);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? { ...m, displayText: slice, streaming: i < fullText.length }
              : m
          )
        );
        if (i >= fullText.length) {
          stopStreaming();
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msgId
                ? { ...m, done: true, streaming: false, displayText: fullText }
                : m
            )
          );
        }
      }, 14);
    },
    [stopStreaming]
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      stopStreaming();

      const userMsg: ChatMessage = {
        id: nextId++,
        role: 'user',
        kind: 'text',
        text: trimmed,
        done: true,
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);
      setSuggestions([]);

      try {
        const data = await aiSearchFaqs('customer', trimmed);

        if (data.faqs && data.faqs.length > 0) {
          const bestFaq = data.faqs[0]; // Already sorted by score desc
          const aiMsgId = nextId++;

          const aiMsg: ChatMessage = {
            id: aiMsgId,
            role: 'assistant',
            kind: 'text',
            text: bestFaq.answer,
            displayText: '',
            streaming: true,
            done: false,
            feedback: null,
          };

          setIsLoading(false);
          setMessages((prev) => [...prev, aiMsg]);
          startStreaming(aiMsgId, bestFaq.answer);

          // Set suggestion chips from other FAQ results
          setSuggestions(getSuggestions(data.faqs, bestFaq));

          // If booking-related, fetch provider card
          if (isBookingRelated(bestFaq)) {
            try {
              const conciergeData = await getConciergeSearch({
                query: trimmed,
              });
              const match = conciergeData?.data?.matches?.[0];
              if (match) {
                const cardMsg: ChatMessage = {
                  id: nextId++,
                  role: 'assistant',
                  kind: 'card',
                  text: '',
                  providerName: match.providerName || 'Provider',
                  providerService: match.serviceTitle || '',
                  rating: match.avg_rating ?? undefined,
                  reviewCount: match.review_count ?? undefined,
                  price: (match as any).priceMin ?? match.price ?? undefined,
                  serviceId: match.serviceId,
                  slug: match.slug,
                  done: true,
                };
                setMessages((prev) => [...prev, cardMsg]);
              }
            } catch {
              // Fail silently — show text answer without card
            }
          }
        } else {
          // No FAQ matches
          const fallbackId = nextId++;
          setIsLoading(false);
          setMessages((prev) => [
            ...prev,
            {
              ...GREETING,
              id: fallbackId,
              text: FALLBACK_TEXT,
              displayText: '',
            },
          ]);
          startStreaming(fallbackId, FALLBACK_TEXT);
          setSuggestions(DEFAULT_SUGGESTIONS);
        }
      } catch {
        setIsLoading(false);
        const errorId = nextId++;
        const errorText =
          "I'm having trouble connecting. Please try again in a moment.";
        setMessages((prev) => [
          ...prev,
          {
            id: errorId,
            role: 'assistant',
            kind: 'text',
            text: errorText,
            displayText: errorText,
            done: true,
            streaming: false,
            feedback: null,
          },
        ]);
        setSuggestions(DEFAULT_SUGGESTIONS);
      }
    },
    [isLoading, startStreaming, stopStreaming]
  );

  const reset = useCallback(() => {
    stopStreaming();
    nextId = 1;
    setMessages([GREETING]);
    setIsLoading(false);
    setSuggestions(['What is HirePanther?', 'How do I book a service?']);
  }, [stopStreaming]);

  const setFeedback = useCallback((msgId: number, value: 'yes' | 'no') => {
    setMessages((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, feedback: value } : m))
    );
  }, []);

  return {
    messages,
    isLoading,
    suggestions,
    send,
    reset,
    setFeedback,
  };
}
